import { defineStore } from 'pinia'
import { ref, computed } from 'vue'
import md5 from 'md5'
import { omit } from 'lodash'

const promptDefaults = {
  title: 'Notice',
  type: 'is-danger'
}

const confirmDefaults = {
  title: 'Notice',
  type: 'is-danger'
}

const alertDefaults = {
  title: 'Alert',
  type: 'is-success'
}

const snackbarDefaults = {
  duration: 4000,
  position: 'is-bottom',
  actionText: null
}

export const useDialogsStore = defineStore('dialogs', () => {
  // State - priority queues (0, 1, 2)
  const queue0 = ref([])
  const queue1 = ref([])
  const queue2 = ref([])
  const active = ref(null)

  // Getters
  const queue = computed(() => {
    return [queue2.value, queue1.value, queue0.value].reduce((accum, q) => {
      if (q.length && !accum) {
        accum = q[0]
        return accum
      }
      return accum
    }, null)
  })

  const getActive = computed(() => active.value)

  // Actions
  function addToQueue(priority, config) {
    config.priority = priority

    if (priority === 0) {
      queue0.value = [...queue0.value, config]
    } else if (priority === 1) {
      queue1.value = [...queue1.value, config]
    } else if (priority === 2) {
      queue2.value = [...queue2.value, config]
    }
  }

  function removeFromQueue(priority, uuid) {
    let targetQueue
    if (priority === 0) targetQueue = queue0
    else if (priority === 1) targetQueue = queue1
    else if (priority === 2) targetQueue = queue2

    if (targetQueue) {
      const idx = targetQueue.value.findIndex(c => c.uuid == uuid)
      if (idx > -1) {
        targetQueue.value.splice(idx, 1)
      }
    }
  }

  function setActive(activeDialog) {
    active.value = activeDialog
  }

  function resetState() {
    queue0.value = []
    queue1.value = []
    queue2.value = []
    active.value = null
  }

  function queueDialog(priority, config) {
    config.uuid = md5(JSON.stringify(omit(config, ['onConfirm','onCancel'])))

    if (active.value && active.value.uuid == config.uuid) {
      return
    }

    let targetQueue
    if (priority === 0) targetQueue = queue0.value
    else if (priority === 1) targetQueue = queue1.value
    else if (priority === 2) targetQueue = queue2.value

    if (targetQueue && !targetQueue.find(c => c.uuid == config.uuid)) {
      for (let [fnKey, fn] of Object.entries({ onConfirm: config.onConfirm, onCancel: config.onCancel})) {
        config[fnKey] = function (...args) {
          const _this = this
          removeFromQueue(priority, config.uuid)
          setActive(null)
          if (fn) fn.call(_this, ...args)
        }
      }
    }

    addToQueue(priority, config)
  }

  function alert(priority, message, config = {}) {
    const mergedConfig = { ...alertDefaults, ...config, message, __type: 'alert' }
    queueDialog(priority, mergedConfig)
  }

  function confirm(priority, message, config) {
    const mergedConfig = { ...confirmDefaults, message, ...config, __type: 'confirm' }
    queueDialog(priority, mergedConfig)
  }

  function prompt(priority = 0, config) {
    const mergedConfig = { ...promptDefaults, ...config, __type: 'prompt'}
    queueDialog(priority, mergedConfig)
  }

  function snackbar(priority = 0, message, config = {}) {
    const mergedConfig = { ...snackbarDefaults, ...config, __type: 'snackbar', message }
    queueDialog(priority, mergedConfig)
  }

  return {
    // State
    queue0,
    queue1,
    queue2,
    active,

    // Getters
    queue,
    active: getActive,

    // Actions
    resetState,
    setActive,
    queueDialog,
    alert,
    confirm,
    prompt,
    snackbar
  }
})
